import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  LoadingSkeleton,
  NoDataSkeleton,
  PaginationTable,
} from '../../../common/components';
import {
  AUDIT_TABLE_COLUMNS,
} from '../constants/auditConstants';
import {
  formatAuditTimestamp,
  formatAuditUser,
  formatAuditDescription,
  formatTechnicalAuditLine,
  getStatusColor,
} from '../utils/formatters';

const TECH_LINE_MAX_CHARS = 48;

const AuditTable = ({
  actions = [],
  isLoading,
  page,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSelectAction,
  onExportAction,
}) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <GeneralTableBox>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {AUDIT_TABLE_COLUMNS.map((column) => {
              const columnProps =
                column.id === 'description'
                  ? {
                      sx: {
                        maxWidth: { xs: '32ch', md: '45ch' },
                        width: { xs: '32ch', md: '45ch' },
                      },
                    }
                  : {};

              return (
                <HeadTableCell key={column.id} {...columnProps}>
                  {column.label}
                </HeadTableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {actions.length > 0 ? (
            actions.map((action) => (
              <TableRow key={action.id} hover>
                <RowTableCell>{formatAuditTimestamp(action.created_at)}</RowTableCell>
                <RowTableCell>{formatAuditUser(action)}</RowTableCell>
                <RowTableCell>{action.module?.display_name || action.module?.key || '—'}</RowTableCell>
                <RowTableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      maxWidth: { xs: '32ch', md: '45ch' },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3,
                      }}
                    >
                      {formatAuditDescription(action)}
                    </Typography>
                    {(() => {
                      const technicalLine = formatTechnicalAuditLine(action);
                      const showTooltip =
                        technicalLine && technicalLine.length > TECH_LINE_MAX_CHARS;
                      return (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          title={showTooltip ? technicalLine : undefined}
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          {technicalLine}
                        </Typography>
                      );
                    })()}
                  </Box>
                </RowTableCell>
                <RowTableCell>
                  <Chip
                    label={action.status_code || '—'}
                    size="small"
                    color={getStatusColor(action.status_code)}
                  />
                </RowTableCell>
                <RowTableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => onSelectAction(action)}>
                        <VisibilityOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar XLSX de esta auditoría">
                      <IconButton
                        size="small"
                        onClick={() => onExportAction?.(action, 'xlsx')}
                      >
                        <TableChartIcon fontSize="small" sx={{ color: '#0e91d3ff' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar PDF de esta auditoría">
                      <IconButton
                        size="small"
                        onClick={() => onExportAction?.(action, 'pdf')}
                      >
                        <PictureAsPdfIcon fontSize="small" sx={{ color: '#0e91d3ff' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </RowTableCell>
              </TableRow>
            ))
          ) : (
            <NoDataSkeleton message="No se encontraron acciones registradas con los filtros seleccionados" />
          )}
        </TableBody>
      </Table>

      {totalCount > rowsPerPage && (
        <PaginationTable
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </GeneralTableBox>
  );
};

export default AuditTable;
